/**
 * useCobroQuickForm
 * ------------------
 * Hook que encapsula el estado y los handlers del formulario rápido de cobro.
 *
 * Responsabilidades:
 *  - Cargar pacientes desde `/api/patients` cuando el diálogo se abre.
 *  - Autoseleccionar un paciente a partir de `prefillName` (matching tolerante).
 *  - Exponer setters atómicos (`setField`, `selectPatient`) sin filtrar
 *    detalles del estado al componente padre.
 *
 * Phase QR2 — extraído de CobroQuickDialog para reducir su complejidad.
 */
import { useEffect, useState, useCallback } from 'react';
import { api, fullName } from './api';
import {
  findPatientMatch, patientAddressLine, buildInitialForm,
} from './cobroQuickHelpers';

export function useCobroQuickForm({
  open,
  prefillName,
  prefillPatient = null,
  defaultConcept,
  defaultAmount = null,
  defaultPaymentMethod = '',
}) {
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState(() =>
    buildInitialForm({
      patient: prefillPatient, prefillName, defaultConcept,
      defaultAmount, defaultPaymentMethod,
    })
  );

  // Reset form + cargar pacientes cada vez que el diálogo se abre.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get('/api/patients').then((r) => {
      if (cancelled) return;
      const listed = Array.isArray(r.data) ? r.data : [];
      // La ficha abierta puede ser antigua y no entrar en la lista limitada
      // de pacientes recientes. Se conserva para no perder sus datos.
      const patientsWithPrefill = prefillPatient && !listed.some((p) => p.id === prefillPatient.id)
        ? [prefillPatient, ...listed]
        : listed;
      setPatients(patientsWithPrefill);
      const match = prefillPatient || findPatientMatch(patientsWithPrefill, prefillName);
      setForm(buildInitialForm({
        patient: match, prefillName, defaultConcept,
        defaultAmount, defaultPaymentMethod,
      }));
    });
    return () => { cancelled = true; };
  }, [open, prefillName, prefillPatient, defaultConcept, defaultAmount, defaultPaymentMethod]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  // `elegido` llega del buscador, que sí encuentra pacientes fuera de la lista
  // precargada. Antes, si el paciente no estaba en esa lista se BORRABA la
  // selección: al elegir un paciente antiguo el cobro se quedaba sin paciente
  // y no había forma de registrarlo.
  const selectPatient = useCallback((id, elegido) => {
    const p = elegido || patients.find((x) => x.id === id);
    if (!id) {
      setForm((prev) => ({ ...prev, patient_id: '' }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      patient_id: id,
      patient_name: p ? fullName(p) : prev.patient_name,
      nif: p ? (p.nif || '') : prev.nif,
      address: p ? patientAddressLine(p) : prev.address,
    }));
  }, [patients]);

  return { patients, form, setField, selectPatient };
}
